import React, { createContext, useContext, useEffect, useState } from 'react';
import type { UserPreferences } from '@metron/shared';
import { useAuth } from './AuthContext.tsx';
import { useApiClient } from '../hooks/useApiClient.ts';
import { createLogger } from '@metron/shared';

const logger = createLogger('PreferencesContext');

interface PreferencesContextType {
  preferences: UserPreferences;
  updatePreferences: (updates: Partial<UserPreferences>) => Promise<void>;
  isLoading: boolean;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const apiClient = useApiClient();
  const [preferences, setPreferences] = useState<UserPreferences>(() => {
    // Initialize from localStorage to avoid theme flash on page load.
    // The API fetch (below) will override this for authenticated users.
    const storedTheme = localStorage.getItem('theme') as 'light' | 'dark' | 'system' | null;
    return {
      theme: storedTheme || 'system',
      sidebarCollapsed: false,
    };
  });
  const [isLoading, setIsLoading] = useState(false);

  // Load preferences from API when user authenticates
  useEffect(() => {
    if (isAuthenticated) {
      loadPreferences();
    }
  }, [isAuthenticated]);

  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement;

    const applyTheme = () => {
      root.classList.remove('light', 'dark');

      if (preferences.theme === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light';
        root.classList.add(systemTheme);
      } else {
        root.classList.add(preferences.theme);
      }
    };

    applyTheme();

    // Also store in localStorage for non-authenticated persistence
    localStorage.setItem('theme', preferences.theme);

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (preferences.theme === 'system') {
        applyTheme();
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [preferences.theme]);

  async function loadPreferences() {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      const data = await apiClient.get<{ data: UserPreferences | null }>('preferences');
      if (data.data) {
        setPreferences(data.data);
      }
    } catch (error) {
      logger.error({ error, action: 'load_preferences_failed' }, 'Failed to load preferences');
      // If it's a network error, the API might not be running
      if (error instanceof TypeError && error.message.includes('fetch')) {
        logger.warn(
          { action: 'api_offline' },
          'API appears to be offline - using default preferences',
        );
      }
      // Token expiration is handled by the API client automatically
    } finally {
      setIsLoading(false);
    }
  }

  async function updatePreferences(updates: Partial<UserPreferences>) {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      const data = await apiClient.put<{ data: UserPreferences }>('preferences', updates);
      setPreferences(data.data);
    } catch (error) {
      logger.error(
        { error, updates, action: 'update_preferences_failed' },
        'Failed to update preferences',
      );
      throw error;
    } finally {
      setIsLoading(false);
    }
  }

  const value = {
    preferences,
    updatePreferences,
    isLoading,
  };

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
}
